/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { useUserId } from '@/api/hooks';
import {
  getLanguages,
  getUserSettings,
  saveUserSettings,
  type HomePreference,
  type LanguageDto,
  type ThemeSetting,
} from '@/api/settings';
import { SUPPORTED_LANGUAGES } from '@/i18n/i18n';
import { useUiStore } from '@/stores/uiStore';
import { Menu } from './Menu';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Human-friendly names for a locale code. Prefers the backend catalog's {@code display_string} (so
 * the list matches the `/settings/languages` API and the Angular app), then the endonym from
 * {@link Intl.DisplayNames}, then the raw code. A secondary English hint is shown when it adds
 * information beyond the primary label.
 */
function describeLanguage(code: string, fallback: string): { primary: string; secondary?: string } {
  const provided =
    fallback && fallback.trim() && fallback.trim().toLowerCase() !== code.toLowerCase()
      ? fallback.trim()
      : undefined;
  try {
    const native = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    const english = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
    const primary = cap(provided || native || code);
    const secondary = english && cap(english) !== primary ? cap(english) : undefined;
    return { primary, secondary };
  } catch {
    return { primary: provided || code };
  }
}

/** Short badge shown on the trigger, e.g. EN, ES-MX, ZH-CN. */
const shortCode = (code: string) => code.toUpperCase();

type ViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => { finished: Promise<void> };
};

/**
 * Applies the language at runtime. When the browser supports the View Transitions API the swap is
 * wrapped in a short crossfade so the whole UI re-labels seamlessly instead of snapping; otherwise
 * it falls back to an instant i18next switch.
 */
async function applyLanguage(
  i18n: { changeLanguage: (code: string) => Promise<unknown> },
  code: string,
): Promise<void> {
  const start = (document as ViewTransitionDoc).startViewTransition;
  if (typeof start === 'function') {
    await start
      .call(document, async () => {
        await i18n.changeLanguage(code);
      })
      .finished.catch(() => {});
    return;
  }
  await i18n.changeLanguage(code);
}

/**
 * Header language switcher. Lists the backend-provided languages (falling back to the bundled
 * locales when the catalog is unavailable), applies the choice at runtime via i18next, and persists
 * it to the user's settings so it is restored on the next visit.
 */
export function LanguageMenu({
  open,
  onOpenChange,
  returnFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where Escape sends focus while the trigger is folded into "More". */
  returnFocus?: () => HTMLElement | null;
}) {
  const { t, i18n } = useTranslation();
  const gmin = useUserId();
  const queryClient = useQueryClient();
  const theme = useUiStore((s) => s.theme);

  const enabled = Boolean(gmin);
  const languagesQuery = useQuery({
    queryKey: ['languages'],
    queryFn: getLanguages,
    enabled,
    retry: false,
    staleTime: 300_000,
  });
  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled,
    retry: false,
  });

  const current = i18n.language;
  // Memoize off the query data (a stable reference) so the fallback array and the Intl.DisplayNames
  // work in `labelled` are not rebuilt on every render.
  const languages = useMemo<LanguageDto[]>(() => {
    const backend = languagesQuery.data ?? [];
    return backend.length
      ? backend
      : SUPPORTED_LANGUAGES.map((code) => ({
          languageId: 0,
          languageCode: code,
          displayString: code,
        }));
  }, [languagesQuery.data]);

  const labelled = useMemo(
    () =>
      languages.map((l) => ({
        code: l.languageCode,
        ...describeLanguage(l.languageCode, l.displayString),
      })),
    [languages],
  );
  const currentLabel = labelled.find((l) => l.code === current)?.primary ?? shortCode(current);

  // Persist alongside the user's existing plant/home/theme so nothing else is disturbed. Fired
  // eagerly (not awaited behind the view transition) so the save can never be lost to a slow
  // crossfade or a quick refresh right after switching.
  const persistLanguage = (code: string) => {
    if (!gmin) return;
    const s = settingsQuery.data;
    void saveUserSettings(gmin, {
      plant: s?.plant?.plantCode ?? 'AR',
      language: code,
      preference: (s?.preferenceCode === 'manufacturer'
        ? 'manufacturer'
        : 'dealer') as HomePreference,
      theme: theme as ThemeSetting,
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['user-settings', gmin] }))
      .catch(() => {
        /* persisting the language is best-effort; the runtime switch already applied */
      });
  };

  const select = (code: string) => {
    onOpenChange(false);
    if (code === current) return;
    persistLanguage(code);
    applyLanguage(i18n, code).catch(() => {
      /* the runtime switch failed (e.g. i18next threw); the previous language stays active */
    });
  };

  return (
    <Menu
      open={open}
      onOpenChange={onOpenChange}
      returnFocus={returnFocus}
      trigger={({ open: isOpen, toggle }) => (
        <button
          className="tb-lang"
          title={`${t('language.title')}: ${currentLabel}`}
          data-tour="language"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`${t('language.title')}: ${currentLabel}`}
          onClick={toggle}
        >
          <Icon name="language" size={20} />
          <span className="tb-lang-code">{shortCode(current)}</span>
        </button>
      )}
    >
      <div className="lang-head">
        <span className="lang-head-icon">
          <Icon name="language" size={20} />
        </span>
        <div className="lang-head-text">
          <div className="lang-head-title">{t('language.title')}</div>
          <div className="lang-head-current">{currentLabel}</div>
        </div>
      </div>
      <div className="lang-list" role="group" aria-label={t('language.title')}>
        {labelled.map((l) => {
          const selected = l.code === current;
          return (
            <button
              key={l.code}
              type="button"
              aria-current={selected ? 'true' : undefined}
              className={`lang-item${selected ? ' on' : ''}`}
              onClick={() => select(l.code)}
            >
              <span className="lang-item-flag">{shortCode(l.code)}</span>
              <span className="lang-item-text">
                <span className="lang-item-name">{l.primary}</span>
                {l.secondary && <span className="lang-item-sub">{l.secondary}</span>}
              </span>
              <span className="lang-item-check" aria-hidden="true">
                {selected && <Icon name="check" size={16} />}
              </span>
            </button>
          );
        })}
      </div>
    </Menu>
  );
}
